import { isEmpty } from 'ramda'
import { RSAA } from 'redux-api-middleware'
import { getApiPath, webStorage } from 'sidecar-utility-library'

import { types, initialState } from './index'
import { types as memberTypes } from 'store/Member'

/**
 * Same-origin via the MFE proxy; the route tries WorkOS, then the legacy login.
 * See route.ts.
 */
export const login = ({ email, password }) => {
  const authBuff = btoa(`${email}:${password}`)
  return {
    [RSAA]: {
      endpoint: '/api/auth/login',
      method: 'POST',
      headers: {
        Authorization: `Basic ${authBuff}`,
      },
      types: [types.LOGIN_REQUEST, types.LOGIN_SUCCESS, types.LOGIN_ERROR],
    },
  }
}

// This action is important because we have several places that use the
// Auth.authToken and we only ever save that when a person logs in. This
// action makes sure that the authToken is available in this store for
// those other functions (like initing LaunchDarkly) after page refresh.
export const loginFromLocal = () => ({ type: types.LOGIN_FROM_LOCAL })

export const logout = ({ message } = {}) => ({
  [RSAA]: {
    endpoint: getApiPath('auth', 'logout'),
    method: 'DELETE',
    types: [
      types.LOGOUT_REQUEST,
      {
        type: types.LOGOUT_SUCCESS,
        meta: { message },
      },
      types.LOGOUT_ERROR,
    ],
  },
})

export const forceLogout = () => ({
  type: types.FORCE_LOGOUT,
})

export const fetchLoginHistory = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      `auth`,
      `members/${webStorage.getItem('sub')}/sessions`,
    ),
    method: 'GET',
    types: [
      types.FETCH_LOGIN_HISTORY_REQUEST,
      types.FETCH_LOGIN_HISTORY_SUCCESS,
      types.FETCH_LOGIN_HISTORY_ERROR,
    ],
  },
})

export const contactUs = ({ firstName, lastName, email, message }) => ({
  [RSAA]: {
    endpoint: getApiPath(`auth`, `contact`),
    method: 'POST',
    body: JSON.stringify({
      firstName,
      lastName,
      email,
      message,
      topic: 'Account',
    }),
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
    },
    types: [
      types.CONTACT_US_REQUEST,
      types.CONTACT_US_SUCCESS,
      {
        type: types.CONTACT_US_ERROR,
        payload: async (_action, _state, res) => {
          if (res.status === 429) {
            const retryAfter = res.headers.get('Retry-After')
            const seconds = retryAfter ? parseInt(retryAfter, 10) : null
            return {
              status: res.status,
              response: {
                message: seconds
                  ? `Too many requests. Please try again in ${seconds} seconds.`
                  : 'Too many requests. Please try again later.',
              },
            }
          }
          const body = await res.json()
          return { status: res.status, response: body }
        },
      },
    ],
  },
})

export const flushAuthError = () => ({
  type: types.FLUSH_AUTH_ERRORS,
})

export const storeAuthedUser = payload => ({
  type: types.STORE_AUTHED_USER,
  payload,
})
export const clearAuthedUser = () => ({
  type: types.CLEAR_AUTHED_USER,
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.LOGIN_REQUEST:
    case types.LOGOUT_REQUEST:
    case types.FETCH_LOGIN_HISTORY_REQUEST:
    case types.CONTACT_US_REQUEST: {
      return {
        ...state,
        busy: true,
      }
    }

    case types.LOGIN_SUCCESS:
    case types.FETCH_AUTH_TOKEN_SUCCESS: {
      return {
        ...state,
        busy: false,
        error: '',
        authToken: payload.token,
        accountMemberAccessMap: payload.accountMemberAccessMap,
        isAuthAttempted: true,
      }
    }

    case types.LOGOUT_SUCCESS: {
      return {
        ...state,
        busy: false,
        message: meta.message,
      }
    }
    case types.FETCH_LOGIN_HISTORY_SUCCESS: {
      return {
        ...state,
        busy: false,
        loginHistory: payload.content,
      }
    }

    case types.LOGIN_ERROR:
    case types.FETCH_LOGIN_HISTORY_ERROR: {
      return {
        ...state,
        busy: false,
        error: payload.response.message,
        isAuthAttempted: true,
      }
    }

    case types.LOGOUT_ERROR:
    case types.CONTACT_US_ERROR: {
      return {
        ...state,
        busy: false,
        error: payload.response.message,
      }
    }

    // This case MUST load ALL NECESSARY stuff from web storage so that
    // anything that relies on that stuff can properly initialize.
    case types.LOGIN_FROM_LOCAL: {
      return {
        ...state,
        authToken: webStorage.getItem('token') || state.authToken,
        accountMemberAccessMap: JSON.parse(
          webStorage.getItem('accountMemberAccessMap') || '{}',
        ),
        isAuthAttempted: true,
      }
    }

    case types.CONTACT_US_SUCCESS: {
      return {
        ...state,
        busy: false,
        meta: meta.message,
      }
    }

    case types.FLUSH_AUTH_ERRORS: {
      return {
        ...state,
        error: '',
      }
    }

    // storing and clearing the authed user is for LD purposes,
    // as such we don't want to update the authedUser if it's
    // already been set. This is to prevent the LD identity call
    // from happening too often. When we logout we clear it so
    // that it can be set again.
    case types.STORE_AUTHED_USER: {
      const hasUser = !isEmpty(state.authedUser)
      if (hasUser) return state

      return {
        ...state,
        authedUser: payload,
      }
    }
    case types.CLEAR_AUTHED_USER: {
      return {
        ...state,
        authedUser: initialState.authedUser,
      }
    }

    // This case pays attention to an action from the member store
    // to save the authed user. The STORE_AUTHED_USER above is kinda
    // strange because it won't save it if it's already populated.
    // I want this to be populated.
    case memberTypes.FETCH_AUTHED_USER_SUCCESS: {
      return {
        ...state,
        authedUser: payload,
      }
    }

    default:
      return state
  }
}
